import * as React from 'react';
import { useId } from '../../../hooks';
import { Styler, StylerProps } from '../../Styler';
import { RadioGroupContext, RadioGroupContextValue } from './RadioGroup.context';

export interface RadioGroupProps extends StylerProps {
    /**
     * The id of the element which labels the group.
     */
    'aria-labelledby'?: string;

    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * The default value to use if the component is not controlled.
     */
    defaultValue?: string;

    /**
     * The `name` attribute which will be applied to each radio button in the group.
     * By default this will be randomly generated.
     */
    name?: string;

    /**
     * Callback fired when a radio button is selected.
     * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when a radio button is selected.
     * @param {string} value The new value.
     */
    onChangeValue?: (value: string) => void;

    /**
     * The value of the selected radio button.
     */
    value?: string;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: unknown;
}

/**
 * A Radio Group allows the user to choose a single option from a set.
 */
export function RadioGroup(props: Readonly<RadioGroupProps>): React.JSX.Element {
    const {
        'aria-labelledby': ariaLabelledbyProp,
        children,
        defaultValue,
        name: nameProp,
        onChange,
        onChangeValue,
        value: valueProp,
        ref,
        ...other
    } = props;

    const name = useId(nameProp);

    const isControlled = valueProp !== undefined;
    const [valueState, setValueState] = React.useState(defaultValue);

    const getValue = React.useCallback(() => {
        return isControlled ? valueProp : valueState;
    }, [isControlled, valueProp, valueState]);

    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement> | string) => {
            if (!isControlled) {
                setValueState(typeof event === 'string' ? event : event.target.value);
            }
            if (typeof event === 'string') {
                onChangeValue?.(event);
            } else {
                onChange?.(event);
            }
        },
        [isControlled, onChange, onChangeValue]
    );

    const contextValue: RadioGroupContextValue = React.useMemo(() => {
        return { name, onChange: handleChange, onChangeValue: handleChange, value: getValue() };
    }, [getValue, handleChange, name]);

    return (
        <RadioGroupContext.Provider value={contextValue}>
            <Styler ref={ref} role="radiogroup" aria-labelledby={ariaLabelledbyProp} {...other}>
                {children}
            </Styler>
        </RadioGroupContext.Provider>
    );
}
